import { adEventProps, cmpState, consentPromptShowing, readAdConsent } from "./ad-metrics.js";
import { dayKey, daysBetween, isCalendarDay } from "../../public/games/_shared/day.js";
import { sendToGa } from "./ga4.js";
import { analyticsAllowed } from "./analytics-consent.js";
/**
 * Browser analytics client (ops/event-tracking-plan.md v1 + CG scorecard additions).
 *
 * - Every event gets the plan's common properties (event_id uuid, ts, schema_version,
 *   anon_id, session_id, platform, device/os/browser/screen/lang, consent, utm/referrer).
 *   The utm/share attribution is the *session's* (sessionStorage, first touch inside the
 *   session wins), so a paid landing keeps being reported after the player navigates on.
 * - Events are batched to POST /v1/events (fetch keepalive). When the page is hidden or
 *   unloading (visibilitychange→hidden, pagehide) the queue is flushed with
 *   navigator.sendBeacon, after giving hide handlers (e.g. the run tracker's game_end quit)
 *   a chance to enqueue.
 * - Analytics consent: if `localStorage["barumba.consent.analytics"] === "denied"` or the
 *   browser sends Global Privacy Control, nothing is queued or sent.
 *
 * Pure enough to test: all browser globals come in through `env`.
 */

export const SCHEMA_VERSION = "1.1";
export const ENDPOINT = "/v1/events";
export const SESSION_IDLE_MS = 30 * 60 * 1000;
export const HEARTBEAT_MS = 60 * 1000;
export const MAX_BATCH = 50;
const MAX_BEACON_BYTES = 60 * 1024;
const MAX_QUEUE = 500;
const ANON_KEY = "barumba.anon";
export const CONSENT_KEY = "barumba.consent.analytics";
/** QA flag: `?qa=1` sets it (localStorage + 1-year cookie), `?qa=0` clears it. */
export const QA_KEY = "bq_qa";
const QA_COOKIE_MAX_AGE = 365 * 24 * 3600;
/** The sharer's opaque link id (random, not the anon_id, never derived from an email or user id). */
export const SHARE_REF_KEY = "barumba.share_ref";
/** First-touch attribution, written once per visitor (first touch wins). */
export const FIRST_TOUCH_KEY = "barumba.first_touch";
/**
 * The session's attribution (sessionStorage, so it lives exactly as long as the tab): the
 * utm_* / share ref the visit landed with, kept so every later page view of the same visit
 * still reports where it came from. First touch inside the session wins.
 */
export const SESSION_TOUCH_KEY = "barumba.session_touch";
/**
 * Functional ad-suppression flag: no identifier, independent of analytics consent. The value is
 * the ms-epoch time of the FIRST paid landing; it is written only when no valid stamp exists, so
 * repeat paid clicks never extend the window. The legacy value "1" (BRM-72) is re-stamped as now.
 */
export const ACQ_PAID_KEY = "barumba.acq_paid";
/** Paid visitors stay ad-free for this long after their first paid landing. */
export const PAID_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
const LEGACY_PAID = "1";
const MIN_PAID_STAMP = Date.UTC(2020, 0, 1);
const MAX_CLOCK_SKEW_MS = 24 * 60 * 60 * 1000;
let paidAcquisition = false;
/** One deferred paid stamp, written as soon as consent settles outside reduced measurement. */
let heldPaidStamp = null;
let paidRetryInstalled = false;

export function _resetPaidAcquisitionForTests() {
  paidAcquisition = false;
  heldPaidStamp = null;
  paidRetryInstalled = false;
}

const paidMedium = (value) => typeof value === "string" && value.toLowerCase() === "paid";
function paidLanding(loc) {
  try { return paidMedium(new URLSearchParams((loc ?? globalThis.location)?.search || "").get("utm_medium")); }
  catch { return false; }
}
function clockMs(now) {
  const t = typeof now === "function" ? now() : now ?? Date.now();
  return Number(t instanceof Date ? t.getTime() : t);
}
/** A plausible stamp: after 2020 and not more than a day in the future. Anything else is garbage. */
const validStamp = (t, now) => Number.isFinite(t) && t >= MIN_PAID_STAMP && t <= now + MAX_CLOCK_SKEW_MS;
const withinWindow = (t, now) => validStamp(t, now) && now - t < PAID_WINDOW_MS;
const parseStamp = (raw) => (typeof raw === "string" && /^\d{1,16}$/.test(raw) ? Number(raw) : NaN);
function paidStorage(storage) {
  try { return storage === undefined ? globalThis.localStorage : storage; }
  catch { return null; }
}

/** The window in which no storage may be written: the consent hold, and reduced measurement. */
function consentUnsettled(win) {
  const scope = win ?? (() => { try { return globalThis.window; } catch { return null; } })();
  return scope?.__barumbaConsentPending === true || ["pending", "denied"].includes(scope?.__barumbaReducedConsent);
}

function writePaidStamp({ landing, storage, t }) {
  const saved = paidStorage(storage);
  try {
    const raw = saved?.getItem(ACQ_PAID_KEY);
    // Re-stamp legacy "1"; on a paid landing, stamp only if nothing valid is stored yet.
    if (raw === LEGACY_PAID || (landing && !validStamp(parseStamp(raw), t))) saved?.setItem(ACQ_PAID_KEY, String(t));
  } catch { /* storage unavailable: memory preserves suppression for this page */ }
}
function installPaidRetry(win) {
  const scope = win ?? (() => { try { return globalThis.window; } catch { return null; } })();
  if (paidRetryInstalled || !scope?.addEventListener) return;
  paidRetryInstalled = true;
  const retry = () => {
    if (!heldPaidStamp || consentUnsettled(scope)) return;
    writePaidStamp(heldPaidStamp);
    heldPaidStamp = null;
  };
  for (const name of ["barumba-consent-change", "barumba-consent-grant"]) scope.addEventListener(name, retry);
}

/**
 * Remember a paid landing before any ad decision, even with analytics opted out or GPC.
 *
 * The stamp waits in memory while consent is unsettled: the boot-time hold before the CMP
 * answers, and EEA/UK reduced measurement (`__barumbaReducedConsent` is `pending` or
 * `denied`). Nothing is written to storage in that window — the rule first touch, session
 * touch and the anon id follow — and the stamp is written as soon as consent settles outside
 * reduced mode. The in-memory flag is what the ad gate reads meanwhile, and it covers this
 * page load and its SPA navigations.
 */
export function markPaidAcquisition({ loc, storage, now, win } = {}) {
  const landing = paidLanding(loc);
  if (landing) paidAcquisition = true; // memory first: survives SPA navigation if storage fails
  const held = { landing, storage, t: clockMs(now) };
  if (consentUnsettled(win)) {
    // Hold the first call, and let a paid landing replace an organic one.
    if (!heldPaidStamp || (landing && !heldPaidStamp.landing)) heldPaidStamp = held;
    installPaidRetry(win);
    return;
  }
  writePaidStamp(held);
}

/**
 * Paid if: in-memory paid landing, current URL utm_medium=paid, a stored first-paid-landing stamp
 * younger than 30 days (legacy "1" counts as now), or legacy first-touch utm_medium=paid captured
 * less than 30 days ago. Missing or garbage values fail safe to organic.
 */
export function isPaidAcquisition({ loc, storage, now, win } = {}) {
  if (paidAcquisition || paidLanding(loc)) return true;
  const t = clockMs(now);
  const saved = paidStorage(storage);
  if (!saved) return false;
  // While consent is unsettled nothing is written; a stored legacy "1" is still read, just
  // not re-stamped.
  const mayWrite = !consentUnsettled(win);
  try {
    const raw = saved.getItem(ACQ_PAID_KEY);
    if (raw === LEGACY_PAID) {
      if (mayWrite) { try { saved.setItem(ACQ_PAID_KEY, String(t)); } catch { /* still paid as of now */ } }
      return true;
    }
    if (withinWindow(parseStamp(raw), t)) return true;
  } catch { /* try legacy attribution independently */ }
  try {
    const ft = JSON.parse(saved.getItem(FIRST_TOUCH_KEY) || "null");
    return paidMedium(ft?.utm_medium) && typeof ft.captured_at === "string" && withinWindow(Date.parse(ft.captured_at), t);
  } catch { return false; }
}
export const TOUCH_KEYS = Object.freeze(["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "share_ref", "share_link"]);
/**
 * The only attribution a reduced-mode row (EEA/UK consent pending or denied) may carry:
 * the campaign tags of this page's landing, which name the ad that paid for the click and
 * identify nobody. Not `utm_term`, not `share_ref` / `ft_*`, not the referrer URL, and
 * never an ad-network click id (`fbclid`, `gclid`, … — those identify a device).
 */
export const REDUCED_TOUCH_KEYS = Object.freeze(["utm_source", "utm_medium", "utm_campaign", "utm_content"]);
/** Keys a reduced-mode row must never carry: identifiers, click ids and the referrer URL. */
const REDUCED_DROP_KEYS = Object.freeze(["anon_id", "user_id", "guest_id", "session_id", "share_ref", "ft_share_ref", "challenge_id", "recipient_run_id", "run_id", "load_id", "invite_id", "utm_term", "referrer"]);
/** A click id: every ad network spells it `<something>clid` (`fbclid`, `gclid`, `ttclid`, `msclkid`, `dclid`, `wbraid`/`gbraid`). */
export const isClickId = (key) => /clid$/i.test(key) || /^(msclkid|wbraid|gbraid)$/i.test(key);
/** Keep a reduced-mode row free of identifiers, click ids, `ft_*`, `ip_*` and the referrer. */
export function stripReducedProps(ev) {
  for (const key of Object.keys(ev)) {
    if (REDUCED_DROP_KEYS.includes(key) || key.startsWith("ip_") || key.startsWith("ft_") || isClickId(key)) delete ev[key];
  }
  return ev;
}
/** Events that also carry the visitor's first-touch `ft_*` fields. */
export const FIRST_TOUCH_EVENTS = Object.freeze(["session_start", "signup_prompt_shown", "signup_prompt_dismissed", "signup_email_sent", "signup_link_clicked", "account_created"]);
const SHARE_REF = /^[0-9A-Za-z]{10}$/;
const REF_ID = /^[A-Za-z0-9_-]{1,64}$/;
const BASE62 = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";

/** `clonk-stack` → `clonk_stack` (plan's game_id format). */
export const gameId = (slug) => String(slug).replace(/-/g, "_");

export function uuid(cryptoImpl = globalThis.crypto) {
  if (cryptoImpl?.randomUUID) return cryptoImpl.randomUUID();
  const b = new Uint8Array(16);
  if (cryptoImpl?.getRandomValues) cryptoImpl.getRandomValues(b);
  else for (let i = 0; i < 16; i += 1) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Host name of a URL string, or null. Host only: no scheme, port, path or query. */
export function hostOf(value) {
  try {
    return value ? new URL(String(value)).hostname.toLowerCase() || null : null;
  } catch {
    return null;
  }
}

/** Longest time-zone name we send (matches the server's schema cap). */
export const TZ_MAX_LENGTH = 64;
const TZ_NAME = /^[A-Za-z_]+(?:\/[A-Za-z0-9_+-]+){0,2}$/;

/**
 * The device's IANA time-zone setting (`Asia/Manila`), or null when the browser has no
 * usable value. A *setting*, not a location: the server maps it to a coarse country as a
 * second opinion next to the GeoIP lookup (carrier NAT and proxies place the IP elsewhere).
 * Never a measurement, never a precise location, and no offset or local time is sent.
 */
export function deviceTimeZone(intl = globalThis.Intl) {
  let tz = null;
  try {
    tz = intl?.DateTimeFormat?.().resolvedOptions?.().timeZone ?? null;
  } catch {
    return null;
  }
  if (typeof tz !== "string") return null;
  const name = tz.trim();
  return name.length <= TZ_MAX_LENGTH && TZ_NAME.test(name) ? name : null;
}

/**
 * The page's visibility at a moment: `visible`, `hidden`, or `prerender` while the browser
 * is pre-rendering it (Chrome's `document.prerendering`). Null when the document cannot say
 * — never guessed, because "visible" is exactly the claim we must not make about a prefetch.
 */
export const VIS_STATES = Object.freeze(["visible", "hidden", "prerender"]);
export function visibilityState(doc) {
  if (!doc) return null;
  if (doc.prerendering === true) return "prerender";
  const state = doc.visibilityState;
  return VIS_STATES.includes(state) ? state : null;
}

/**
 * Was this page ever actually on screen, and when did it first get there?
 *
 * Meta's Android in-app browser prefetches ad landing pages: the Oct 21 analysis found 86 of
 * 111 "loaded but never started" sessions gone inside 2 s, with their events arriving in the
 * hide beacon — the signature of a page that was never looked at. Nothing in the schema
 * could tell that from a real player who left, so this watch records the visibility at load,
 * the time the page first became visible (if ever) and how long it was on screen in total.
 *
 * `start(t)` installs the listeners; without them the watch still reports the load state.
 *
 * The watch must therefore exist before the application bundle does: a prefetched landing
 * that the player opens while the modules are still parsing is already `visible` by the time
 * a tracker could look, which reports it as a page that loaded visible at 0 ms and loses the
 * prefetch entirely. `src/error-bootstrap.js` starts one in the blocking head script and
 * hands it to the tracker that adopts it; `from` continues an existing watch's record
 * (`snapshot()`) instead of reading the document again.
 */
export function createVisibilityWatch({ win, doc, startedAt = 0, from = null } = {}) {
  const stateAtLoad = from ? from.stateAtLoad : visibilityState(doc);
  const prerender = stateAtLoad === "prerender";
  let everVisible = from ? from.everVisible === true : stateAtLoad === "visible";
  let firstVisibleAt = from ? from.firstVisibleAt ?? null : (everVisible ? startedAt : null);
  let visibleSince = from ? from.visibleSince ?? null : (everVisible ? startedAt : null);
  let visibleMs = from ? Math.max(0, from.visibleMs || 0) : 0;
  let listening = false;
  const offs = [];

  function sample(t) {
    const state = visibilityState(doc);
    if (state === "visible") {
      if (!everVisible) {
        everVisible = true;
        firstVisibleAt = t;
      }
      if (visibleSince === null) visibleSince = t;
      return;
    }
    if (visibleSince !== null) {
      visibleMs += Math.max(0, t - visibleSince);
      visibleSince = null;
    }
  }

  return {
    stateAtLoad,
    prerender,
    /**
     * This watch's time origin: the document's load when the head bootstrap created it.
     * Every "ms since the page started" field measures from here, so a watch adopted by a
     * tracker (or by a play view minutes later) keeps reporting the document's own clock.
     */
    startedAt,
    get everVisible() { return everVisible; },
    get firstVisibleAt() { return firstVisibleAt; },
    /** The document's visibility right now (null when it cannot say). */
    state: () => visibilityState(doc),
    /** Foreground milliseconds up to `t`, including the stretch in progress. */
    visibleMs: (t) => visibleMs + (visibleSince !== null ? Math.max(0, t - visibleSince) : 0),
    /** Everything this watch has recorded, for a second watch to continue from (`from`). */
    snapshot: () => ({ stateAtLoad, everVisible, firstVisibleAt, visibleSince, visibleMs }),
    sample,
    reset(t) {
      everVisible = visibilityState(doc) === "visible";
      firstVisibleAt = everVisible ? t : null;
      visibleSince = everVisible ? t : null;
      visibleMs = 0;
    },
    start(now) {
      // The head bootstrap already listens; a tracker adopting its watch must not double up.
      if (listening) return;
      listening = true;
      const listen = (target, event, handler) => {
        if (!target?.addEventListener) return;
        target.addEventListener(event, handler);
        offs.push(() => target.removeEventListener?.(event, handler));
      };
      const onChange = () => sample(now());
      listen(doc, "visibilitychange", onChange);
      // A pre-rendered page becomes a real one here, which is also its first visible moment.
      listen(doc, "prerenderingchange", onChange);
      listen(win, "pageshow", onChange);
    },
    stop() {
      listening = false;
      for (const off of offs.splice(0)) {
        try { off(); } catch { /* the document is already gone */ }
      }
    },
  };
}

/**
 * Traffic-source signals for session_start: webdriver, cross-site referrer host, the
 * device time zone, and whether the page is framed (and by whom: ancestorOrigins[0], else
 * the referrer).
 */
export function trafficSignals({ win, doc, nav, loc, intl } = {}) {
  const out = { webdriver: nav?.webdriver === true };
  const inApp = inAppBrowser(nav?.userAgent);
  if (inApp) out.in_app = inApp;
  const tz = deviceTimeZone(intl ?? win?.Intl ?? globalThis.Intl);
  if (tz) out.tz = tz;
  const ref = hostOf(doc?.referrer);
  const own = String(loc?.hostname || String(loc?.host || "").replace(/:\d+$/, "")).toLowerCase();
  if (ref && ref !== own) out.referrer_host = ref;
  let framed = false;
  try {
    framed = Boolean(win) && win.top !== win;
  } catch {
    framed = true; // cross-origin top: reading it threw, so we are framed
  }
  out.in_iframe = framed;
  if (framed) {
    let embed = null;
    try {
      embed = hostOf(loc?.ancestorOrigins?.[0]);
    } catch {
      embed = null;
    }
    embed = embed || ref || hostOf(doc?.referrer);
    if (embed) out.embed_host = embed;
  }
  return out;
}

/**
 * Is this a QA-tagged browser (the `bq_qa=1` cookie a `?qa=1` visit sets)? The server hides
 * that browser's runs from every public board, so the results card tags your row "QA (hidden)".
 * Read-only: never sets or clears the flag (qaFlag does that).
 */
export function qaSession(doc = globalThis.document) {
  try { return /(?:^|;\s*)bq_qa=1(?:;|$)/.test(String(doc?.cookie || "")); } catch { return false; }
}

/** Read `?qa=` and the persisted QA flag; `?qa=1` persists it, `?qa=0` clears it. */
export function qaFlag({ doc, loc, storage } = {}) {
  let qa = null;
  try {
    qa = new URLSearchParams(loc?.search || "").get("qa");
  } catch {
    qa = null;
  }
  const secure = loc?.protocol === "https:" ? "; Secure" : "";
  let on = qa === "1";
  if (qa !== "1" && qa !== "0") {
    try { on = storage?.getItem(QA_KEY) === "1"; } catch { /* storage blocked */ }
    try { on ||= /(?:^|;\s*)bq_qa=1(?:;|$)/.test(String(doc?.cookie || "")); } catch { /* cookies blocked */ }
  }
  if (on || qa === "0") {
    try {
      if (on) storage?.setItem(QA_KEY, "1");
      else storage?.removeItem?.(QA_KEY);
    } catch {
      /* storage blocked: the cookie still carries it */
    }
    try {
      if (doc) doc.cookie = `${QA_KEY}=${on ? "1" : ""}; Max-Age=${on ? QA_COOKIE_MAX_AGE : 0}; Path=/; SameSite=Lax${secure}`;
    } catch {
      /* cookies blocked */
    }
  }
  return on;
}

/** 10 random base62 chars. */
export function randomRef(cryptoImpl = globalThis.crypto) {
  const b = new Uint8Array(10);
  if (cryptoImpl?.getRandomValues) cryptoImpl.getRandomValues(b);
  else for (let i = 0; i < b.length; i += 1) b[i] = Math.floor(Math.random() * 256);
  return [...b].map((x) => BASE62[x % 62]).join("");
}

/** This visitor's stable share_ref (created on first use; per page only if storage is blocked). */
export function shareRef({ storage = (() => { try { return globalThis.localStorage; } catch { return null; } })(), cryptoImpl = globalThis.crypto } = {}) {
  try {
    const saved = storage?.getItem(SHARE_REF_KEY);
    if (saved && SHARE_REF.test(saved)) return saved;
  } catch {
    /* storage blocked */
  }
  const ref = randomRef(cryptoImpl);
  try {
    storage?.setItem(SHARE_REF_KEY, ref);
  } catch {
    /* storage blocked */
  }
  return ref;
}

/** `ft_*` event props from a stored first touch. */
export function firstTouchProps(ft) {
  if (!ft || typeof ft !== "object") return {};
  const out = {};
  for (const key of [...TOUCH_KEYS, "referrer"]) {
    if (typeof ft[key] === "string" && ft[key]) out[`ft_${key}`] = ft[key];
  }
  if (typeof ft.captured_at === "string") out.ft_captured_at = ft.captured_at.slice(0, 40);
  return out;
}

/**
 * The attribution for this visit: the value stored for the session wins (the session's first
 * touch), and a key it does not have yet is taken from the current URL. So a Meta landing on
 * `/play/cat-merge?utm_source=meta…` keeps reporting `meta` on the results sheet, a reload or
 * the next game page, and a later URL with no utm_* never clears it — nothing is overwritten
 * with an empty value. Values are capped at 100 chars, like the landing ones.
 */
export function mergeSessionTouch(stored, current) {
  const out = {};
  for (const key of TOUCH_KEYS) {
    const value = [stored?.[key], current?.[key]].find((v) => typeof v === "string" && v !== "");
    if (value) out[key] = value.slice(0, 100);
  }
  return out;
}

const shortId = (prefix, cryptoImpl) => `${prefix}_${uuid(cryptoImpl).replace(/-/g, "").slice(0, 16)}`;

/** Coarse device facts from the user agent (no fingerprinting beyond the plan's fields). */
/**
 * Which Meta in-app browser this is, from its User-Agent, or null. The Facebook, Instagram,
 * Messenger and Threads apps open ad links in their own WebView and add a token to the UA
 * (`FBAN/` / `FBAV/` / `FB_IAB/` on Facebook and Messenger, `Instagram 3xx`, `Barcelona` on
 * Threads). On session_start as `in_app`, so a paid landing from the feed can be told from
 * the same phone's real Chrome or Safari, which the `browser` field cannot do.
 */
export function inAppBrowser(ua = "") {
  const s = String(ua);
  if (/\bInstagram\b/.test(s)) return "instagram";
  if (/\bBarcelona\b/.test(s)) return "threads";
  if (/FBAN\/(?:Messenger|MessengerForiOS)|FB_IAB\/MESSENGER|\bMessengerLite/i.test(s)) return "messenger";
  if (/FBAN\/|FBAV\/|FB_IAB\/|FBIOS|FB4A/.test(s)) return "facebook";
  return null;
}

export function deviceInfo(ua = "") {
  const s = String(ua);
  const device_type = /iPad|Tablet|PlayBook|Silk|(Android(?!.*Mobile))/i.test(s) ? "tablet" : /Mobi|iPhone|iPod|Android/i.test(s) ? "mobile" : s ? "desktop" : "unknown";
  const os = /iPhone|iPad|iPod/i.test(s) ? "iOS" : /Android/i.test(s) ? "Android" : /CrOS/i.test(s) ? "ChromeOS" : /Windows/i.test(s) ? "Windows" : /Mac OS X|Macintosh/i.test(s) ? "macOS" : /Linux/i.test(s) ? "Linux" : "other";
  const browser = /Edg\//.test(s) ? "Edge" : /SamsungBrowser/.test(s) ? "Samsung" : /Firefox|FxiOS/.test(s) ? "Firefox" : /OPR\//.test(s) ? "Opera" : /Chrome|CriOS/.test(s) ? "Chrome" : /Safari/.test(s) ? "Safari" : "other";
  return { device_type, os, browser };
}

function readJson(storage, key) {
  try {
    const raw = storage?.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJson(storage, key, value) {
  try {
    storage?.setItem(key, JSON.stringify(value));
  } catch {
    /* storage blocked: the id lives for this page only */
  }
}

/** A storage object that may throw on access (blocked cookies, sandboxed iframe). */
const safeStore = (get) => {
  try {
    return get();
  } catch {
    return null;
  }
};

/** Analytics consent: a stored opt-out or Global Privacy Control switches everything off. */
export function analyticsConsent(options = {}) {
  return analyticsAllowed(options);
}

const CHALLENGE_ID = /^ch_[0-9a-f]{48}$/;
const PLAY_PATH = /^\/play\/[a-z0-9-]+$/;
const JOIN_PATH = /^\/join\/[^/?#]+$/;

/**
 * Which kind of share link a landing is (the `share_link` enum, schema 1.1), or null:
 * `/` → recap; `/join/<code>` → invite; `/play/<slug>` → challenge (exactly one valid
 * `challenge=ch_…`, or a legacy `vs=` link), card (exactly one `via=card`) or play. Only
 * the kind is derived: the challenge id and the invite code never leave the URL.
 */
export function shortLinkKind(pathname, params) {
  if (pathname === "/") return "recap";
  if (JOIN_PATH.test(pathname)) return "invite";
  if (!PLAY_PATH.test(pathname)) return null;
  const challenges = params.getAll("challenge");
  if (challenges.length === 1 && CHALLENGE_ID.test(challenges[0])) return "challenge";
  if (challenges.length === 0 && params.getAll("vs").length === 1) return "challenge";
  const via = params.getAll("via");
  if (via.length === 1 && via[0] === "card" && challenges.length === 0) return "card";
  return "play";
}

/** A page path safe for analytics: an invite's `/join/<code>` (a bearer secret) becomes `/join/_`. */
export function safePath(path) {
  return String(path || "/").replace(/^\/join\/[^?#]*/, "/join/_");
}

/**
 * What one URL says about where the visit came from: `utm_*` (plus a valid opaque
 * `share_ref`), a short link's ref and path context, and a cross-site referrer. Attribution
 * only — no identity, nothing derived from an email or a user id — capped at 100 chars
 * (500 for the referrer URL).
 */
export function landingTouch({ doc, loc } = {}) {
  const params = new URLSearchParams(loc?.search || "");
  const touch = {};
  for (const key of TOUCH_KEYS) {
    if (key === "share_link") continue; // Derived only from a short-link landing.
    const value = params.get(key);
    // Keep only a valid opaque share_ref.
    if (value && (key !== "share_ref" || REF_ID.test(value))) touch[key] = value.slice(0, 100);
  }
  // Short links carry only the opaque ref and path context; UTMs are server-only.
  const shortRef = params.get("s");
  const hasLongAttribution = [...params.keys()].some(key => key.startsWith("utm_") || key === "share_ref");
  const shareLink = shortLinkKind(loc?.pathname || "", params);
  if (!hasLongAttribution && params.getAll("s").length === 1 && REF_ID.test(shortRef || "") && shareLink) {
    touch.share_ref = shortRef;
    touch.share_link = shareLink;
  } else if (!hasLongAttribution && shareLink === "challenge" && params.has("vs")) {
    // A legacy ?vs= challenge link (no sharer ref) is still a challenge visit.
    touch.share_link = "challenge";
  }
  try {
    const ref = doc?.referrer ? new URL(doc.referrer) : null;
    // Same-origin SEO landings retain their query in the browser referrer. Recover campaign
    // tags on Play without storing them while the landing's consent is still pending.
    if (ref && ref.origin === loc?.origin && /^\/games(?:\/|$)/.test(ref.pathname) && /^\/play(?:\/|$)/.test(loc?.pathname || "") && !hasLongAttribution && !touch.share_link) {
      for (const key of TOUCH_KEYS.filter((key) => key.startsWith("utm_"))) {
        const value = ref.searchParams.get(key);
        if (value) touch[key] = value.slice(0, 100);
      }
    }
    if (ref && ref.host !== loc?.host) {
      touch.referrer = `${ref.origin}${ref.pathname}`.slice(0, 500);
      touch.portal_host = ref.hostname;
    }
  } catch {
    /* bad referrer */
  }
  return touch;
}

/**
 * Persist this landing's attribution and return what events should carry:
 * `{ touch, firstTouch, sessionTouch, consent }`.
 *
 * First touch (localStorage) is written once per visitor and never overwritten. The
 * session's touch (sessionStorage, so it lives exactly as long as the tab) keeps the first
 * value it saw and a later URL only fills in keys it has not seen yet, so a URL with no
 * `utm_*` never clears the visit's campaign. Nothing is written without analytics consent.
 */
function storeTouch({ touch, nav, storage, sessionStore, now, win }) {
  // EEA/UK reduced measurement (consent pending or denied) stores nothing: those events carry
  // the landing's campaign tags from memory instead (track()'s reducedTouch).
  const reduced = ["pending", "denied"].includes(win?.__barumbaReducedConsent);
  const consent = !reduced && analyticsConsent({ nav, storage, win, requiresConsent: win?.__barumbaConfig?.requiresConsent === true });
  let firstTouch = readJson(storage, FIRST_TOUCH_KEY);
  if (!firstTouch && consent) {
    firstTouch = { ...touch, captured_at: now().toISOString() };
    delete firstTouch.portal_host;
    if (firstTouch.referrer) firstTouch.referrer = firstTouch.referrer.slice(0, 100);
    writeJson(storage, FIRST_TOUCH_KEY, firstTouch);
  }
  let sessionTouch = { ...touch };
  if (consent) {
    const storedTouch = readJson(sessionStore, SESSION_TOUCH_KEY);
    const merged = mergeSessionTouch(storedTouch, touch);
    sessionTouch = { ...touch, ...merged };
    if (Object.keys(merged).length && JSON.stringify(merged) !== JSON.stringify(storedTouch)) writeJson(sessionStore, SESSION_TOUCH_KEY, merged);
  }
  return { touch, firstTouch, sessionTouch, consent };
}

/**
 * Capture the current URL's attribution on page load, without building a tracker.
 *
 * The server-rendered SEO pages (`src/site-analytics.js`) create a tracker only when there
 * is something to track, so a visitor who lands on
 * `/games/cat-merge?utm_source=meta&utm_medium=paid…` and taps Play arrives at `/play/…`
 * with no `utm_*` left in the URL: without this call the whole visit, including every load
 * funnel event, is unattributable. Calling it is cheap (two storage reads) and consent-gated
 * inside, and it is idempotent — the stores keep their first value.
 */
export function captureAttribution(env = {}) {
  const win = env.window ?? globalThis.window;
  const doc = env.document ?? win?.document;
  return storeTouch({
    touch: landingTouch({ doc, loc: env.location ?? win?.location }),
    nav: env.navigator ?? win?.navigator,
    storage: env.localStorage ?? safeStore(() => win?.localStorage),
    sessionStore: env.sessionStorage ?? safeStore(() => win?.sessionStorage),
    now: env.now ?? (() => new Date()),
    win,
  });
}

/**
 * @param {object} env  { window, document, navigator, location, localStorage, fetch, crypto, now }
 *   plus `visibility` / `startedAt`: a visibility watch and the time origin captured by the
 *   blocking head script (src/error-bootstrap.js), adopted so the page's own load — not this
 *   module's first execution — is what the visibility and timing fields measure.
 */
export function createTracker(env = {}) {
  const win = env.window ?? globalThis.window;
  const doc = env.document ?? win?.document;
  const nav = env.navigator ?? win?.navigator;
  const loc = env.location ?? win?.location;
  const storage = env.localStorage ?? (() => { try { return win?.localStorage; } catch { return null; } })();
  const sessionStore = env.sessionStorage ?? (() => { try { return win?.sessionStorage; } catch { return null; } })();
  const fetchImpl = env.fetch ?? win?.fetch?.bind(win);
  const cryptoImpl = env.crypto ?? win?.crypto ?? globalThis.crypto;
  const now = env.now ?? (() => new Date());
  const endpoint = env.endpoint ?? ENDPOINT;

  const queue = [];
  const pendingEvents = [];
  const hideHandlers = new Set();
  let timer = null;
  let started = false;
  let sessionStarted = false;
  let missedGaSessionStart = null;
  let resetSession = false;
  let consentEpoch = 0;
  const reducedEventIds = new Map();
  // is_member = an active PAID membership (state.membership.active), not "signed in".
  let isMember = false;
  let childDirected = false;

  // anon_id: first-party, persistent. firstSeen is a PT day for days_since_first_seen.
  const today = () => dayKey(now());
  // Reduced measurement (EEA/UK, consent pending or denied) has no persistent id at all: no
  // read, no mint, no write. A later grant mints one in consentAnalytics().
  const idAllowed = () => !["pending", "denied"].includes(win?.__barumbaReducedConsent) &&
    analyticsConsent({ nav, storage, win, requiresConsent: win?.__barumbaConfig?.requiresConsent === true });
  let anon = idAllowed() ? readJson(storage, ANON_KEY) : null;
  let isNewUser = !anon?.id;
  if (anon && !/^[A-Za-z0-9_-]{1,64}$/.test(anon.id || "")) anon = null;
  if (!anon && idAllowed()) {
    anon = { id: shortId("a", cryptoImpl), firstSeen: today() };
    writeJson(storage, ANON_KEY, anon);
  }

  // session_id: new per tab load, and again after 30 min without events.
  let sessionId = shortId("s", cryptoImpl);
  // The session starts when the document did, not when this module finally ran: the head
  // bootstrap (src/error-bootstrap.js) passes its own time origin, so every "ms since the
  // page started" field measures from before the bundle executed.
  let sessionStartedAt = Number.isFinite(env.startedAt) ? env.startedAt : now().getTime();
  let lastEventAt = sessionStartedAt;
  let gamesPlayed = 0;
  // One session_end per session, whichever leave reports it. A `hidden` leave sends it with
  // this id so a tab the OS kills without a pagehide (common on Android) is still counted;
  // the real pagehide re-sends the same id and the server replaces the row.
  let sessionEndEventId = uuid(cryptoImpl);
  let sessionWasReduced = false;

  // Was the page ever on screen, and when did it get there? (Prefetched Meta landings.)
  // Adopted from the head bootstrap when there is one, so a landing that was hidden at load
  // and became visible before this bundle ran still reports the hidden start and the real
  // first-visible moment instead of "visible at 0 ms".
  const visibility = env.visibility ?? createVisibilityWatch({ win, doc, startedAt: sessionStartedAt });

  // Session counters reset independently of the document-level load funnel, but they start
  // from the same record: `from` continues the adopted watch rather than re-reading the
  // document, which by now says "visible".
  const sessionVisibility = createVisibilityWatch({ win, doc, startedAt: sessionStartedAt, from: visibility.snapshot?.() ?? null });

  // Both watches listen from the moment they exist, not from start(). An early client_error
  // builds this tracker through window.__barumbaErrors long before the application starts it,
  // and a hidden → visible transition in between belongs to neither watch's snapshot nor to
  // any listener — the session would report ever_visible:false for a page the player really
  // did open. start() is a no-op on a watch that is already listening, so the head
  // bootstrap's adopted watch is never subscribed twice.
  visibility.start(() => now().getTime());
  sessionVisibility.start(() => now().getTime());

  // Attribution for this visit (see captureAttribution): the landing's utm_* / share ref,
  // the visitor's first touch, and the session's first touch — which the SEO pages may
  // already have stored for this tab before the SPA ever loaded.
  const touch = landingTouch({ doc, loc });
  let { firstTouch, sessionTouch } = storeTouch({ touch, nav, storage, sessionStore, now, win });
  let ftProps = firstTouchProps(firstTouch);

  const device = deviceInfo(nav?.userAgent);
  // Server adds ua_class / ip_class / ip_hash / geo_country / tz_country and ORs is_internal with its own signals.
  const signals = trafficSignals({ win, doc, nav, loc, intl: env.Intl });
  let internal = qaFlag({ doc, loc, storage }) || signals.webdriver;

  // Drop the in-memory id, queued events and session so a later grant starts fresh.
  function forgetIdentity() {
    consentEpoch += 1;
    missedGaSessionStart = null;
    anon = null;
    queue.length = 0;
    if (sessionStarted) {
      sessionStarted = false;
      resetSession = true;
    }
  }

  function reducedMode() {
    if (!["pending", "denied"].includes(win?.__barumbaReducedConsent) || nav?.globalPrivacyControl === true) return null;
    try {
      if (storage?.getItem?.(CONSENT_KEY) === "denied" && storage?.getItem?.("barumba.consent.analytics.source") !== "cmp") return null;
    } catch { return null; }
    return win.__barumbaReducedConsent;
  }

  /**
   * The landing's campaign tags, for a reduced-mode row: `utm_source` / `utm_medium` /
   * `utm_campaign` / `utm_content` of the current URL, or — on a same-origin `/games` →
   * `/play` hop — recovered from the referrer by `landingTouch`. Each is already capped at
   * 100 chars and only present keys are returned, so an organic visit adds nothing.
   */
  function reducedTouch() {
    const out = {};
    for (const key of REDUCED_TOUCH_KEYS) {
      const value = touch?.[key];
      if (typeof value === "string" && value) out[key] = value.slice(0, 100);
    }
    return out;
  }

  function pendingMeasurement() {
    if (win?.__barumbaConsentPending !== true || nav?.globalPrivacyControl === true) return false;
    try {
      return storage?.getItem?.(CONSENT_KEY) !== "denied" &&
        !analyticsConsent({ nav, storage, win, requiresConsent: win?.__barumbaConfig?.requiresConsent === true });
    } catch { return false; }
  }

  function consentAnalytics() {
    if (reducedMode()) return false;
    if (pendingMeasurement()) return false;
    const allowed = analyticsConsent({ nav, storage, win, requiresConsent: win?.__barumbaConfig?.requiresConsent === true });
    if (!allowed) {
      let choice;
      try { choice = storage?.getItem?.(CONSENT_KEY); } catch { /* unavailable */ }
      if (choice === "denied" || nav?.globalPrivacyControl === true) pendingEvents.length = 0;
      forgetIdentity();
      return false;
    }
    const saved = readJson(storage, ANON_KEY);
    if (saved && /^[A-Za-z0-9_-]{1,64}$/.test(saved.id || "")) {
      if (!anon) isNewUser = false;
      if (anon && anon.id !== saved.id) {
        // Concurrent grants can both mint an id; the last stored id wins.
        for (const event of queue) if (!event.consent_state) event.anon_id = saved.id;
      }
      anon = saved;
    }
    if (!anon) {
      anon = { id: shortId("a", cryptoImpl), firstSeen: today() };
      isNewUser = true;
    }
    if (allowed && !readJson(storage, ANON_KEY)?.id) writeJson(storage, ANON_KEY, anon);
    return allowed;
  }

  // Invalidate in-flight batches even if consent is granted again before they settle.
  let handlingConsentChange = false;
  let wasReduced = Boolean(reducedMode());
  const onConsentChange = () => {
    if (handlingConsentChange) return;
    handlingConsentChange = true;
    try {
      if (reducedMode()) {
        if (!wasReduced) consentEpoch += 1;
        // A withdrawal must discard any normal events buffered before the CMP answer.
        for (let i = queue.length - 1; i >= 0; i--) if (!queue[i].consent_state) queue.splice(i, 1);
      }
      wasReduced = Boolean(reducedMode());
      if (sessionStarted && Boolean(reducedMode()) !== sessionWasReduced && !pendingMeasurement()) {
        sessionStarted = false;
        resetSession = true;
        sessionEndEventId = uuid(cryptoImpl);
      }
      if (reducedMode() || consentAnalytics()) {
        const held = pendingEvents.splice(0);
        for (const args of held) {
          if (args[0] === "session_start" && !reducedMode()) {
            sessionStarted = false;
            startSession();
          } else track(...args);
        }
        if (reducedMode() && sessionStarted) sessionWasReduced = true;
        if (started && !sessionStarted) startSession();
        if (reducedMode() || held.some(([event]) => event === "client_error")) flush({ beacon: win?.__barumbaConsentExit === true });
      }
      else if (!win?.__barumbaConsentPending) pendingEvents.length = 0;
    } finally { handlingConsentChange = false; }
  };
  win?.addEventListener?.("barumba-consent-change", onConsentChange);
  // The tracker can start before Google vendor consent allows the GA bootstrap to load.
  win?.addEventListener?.("barumba-ga-ready", () => {
    if (!missedGaSessionStart || !sessionStarted || !consentAnalytics()) return;
    if (sendToGa(missedGaSessionStart, win)) missedGaSessionStart = null;
  });
  win?.addEventListener?.("storage", (event) => {
    if (event?.key === CONSENT_KEY && event.newValue === "denied") {
      // Act on the denial itself: storage may already read "granted" again by the time this
      // async event arrives, and the pre-denial id must not survive into that grant.
      pendingEvents.length = 0;
      forgetIdentity();
      consentAnalytics();
    } else if (event?.key === CONSENT_KEY && event.newValue === "granted") {
      onConsentChange();
    }
  });

  function common() {
    // Pick up QA enrollment from another tab or an SPA navigation; keep it for this session.
    internal ||= qaFlag({ doc, loc, storage });
    return {
      schema_version: SCHEMA_VERSION,
      anon_id: anon.id,
      session_id: sessionId,
      platform: "owned_web",
      ...device,
      screen_w: Math.round(win?.screen?.width || 0),
      screen_h: Math.round(win?.screen?.height || 0),
      lang: String(nav?.language || "und").slice(0, 35),
      is_member: isMember,
      // Observe the current ad settings; ad_request also carries the full consent snapshot.
      consent_ads: childDirected ? "child" : readAdConsent(win).consent_ads,
      consent_analytics: true,
      ...sessionTouch,
      is_internal: internal,
    };
  }

  function rotateSessionIfIdle(t) {
    if (t - lastEventAt < SESSION_IDLE_MS) return;
    sessionId = shortId("s", cryptoImpl);
    sessionEndEventId = uuid(cryptoImpl);
    sessionStartedAt = t;
    sessionVisibility.reset(t);
    gamesPlayed = 0;
    lastEventAt = t;
    enqueue("session_start", sessionStartProps());
  }

  function sessionStartProps() {
    const days = isCalendarDay(anon.firstSeen) ? Math.max(0, daysBetween(anon.firstSeen, today())) : 0;
    const props = {
      landing_page: safePath(loc?.pathname || "/").slice(0, 500),
      is_new_user: isNewUser && days === 0,
      days_since_first_seen: days,
      ...signals,
      is_internal: internal,
      ...ftProps,
    };
    // Visibility at load: a landing that was hidden (or being pre-rendered) when it loaded
    // is a prefetch until it proves otherwise, not a player who bounced.
    if (visibility.stateAtLoad) props.vis_state = visibility.stateAtLoad;
    props.prerender = visibility.prerender;
    return props;
  }

  /** How long the page was really on screen, and when it first got there. */
  function visibilityProps(t) {
    const props = { ever_visible: sessionVisibility.everVisible, visible_s: Math.max(0, Math.round(sessionVisibility.visibleMs(t) / 1000)) };
    if (sessionVisibility.firstVisibleAt !== null) {
      props.first_visible_ms = Math.min(FUNNEL_MAX_MS, Math.max(0, Math.round(sessionVisibility.firstVisibleAt - sessionStartedAt)));
    }
    return props;
  }

  function enqueue(event, props = {}, { eventId } = {}) {
    const t = now();
    const ev = { event, event_id: eventId || uuid(cryptoImpl), ts: t.toISOString(), ...common(), ...props };
    // Per-event props (including iframe relays) cannot lower a session's internal flag.
    ev.is_internal = internal || ev.is_internal === true;
    queue.push(ev);
    if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);
    lastEventAt = t.getTime();
    // GA4 is an extra sink (src/lib/ga4.js): after the event is queued, never able to throw
    // into this pipeline, and a no-op when GA_MEASUREMENT_ID left gtag off the page.
    try {
      const sent = sendToGa(ev, win);
      if (event === "session_start") missedGaSessionStart = sent ? null : ev;
    } catch { /* GA is optional */ }
    return ev;
  }

  /** Queue an event. Returns the event (or null when consent is denied). */
  function track(event, props = {}, opts = {}) {
    const reduced = reducedMode();
    if (!reduced && pendingMeasurement()) {
      pendingEvents.push([event, props, opts]);
      if (pendingEvents.length > MAX_QUEUE) pendingEvents.shift();
      return null;
    }
    if (!reduced && !consentAnalytics()) {
      let choice;
      try { choice = storage?.getItem?.(CONSENT_KEY); } catch { return null; }
      if (win?.__barumbaConsentPending === true && nav?.globalPrivacyControl !== true && choice !== "denied") {
        pendingEvents.push([event, props, opts]);
        if (pendingEvents.length > MAX_QUEUE) pendingEvents.shift();
      }
      return null;
    }
    if (reduced) {
      if (opts.eventId && !reducedEventIds.has(opts.eventId)) reducedEventIds.set(opts.eventId, uuid(cryptoImpl));
      const ev = { ...props, event, event_id: opts.eventId ? reducedEventIds.get(opts.eventId) : uuid(cryptoImpl), ts: now().toISOString(),
        schema_version: SCHEMA_VERSION, platform: "owned_web", ...device,
        screen_w: Math.round(win?.screen?.width || 0), screen_h: Math.round(win?.screen?.height || 0),
        lang: String(nav?.language || "und").slice(0, 35), is_member: false,
        consent_ads: childDirected ? "child" : readAdConsent(win).consent_ads,
        // The campaign that paid for this click, from the in-memory landing attribution:
        // reduced mode stores nothing, so `touch` (not sessionTouch) is the only source.
        ...reducedTouch(),
        consent_analytics: false, consent_state: reduced, ...cmpState(win) };
      stripReducedProps(ev);
      if (event === "game_start") gamesPlayed += 1;
      queue.push(ev);
      if (queue.length >= MAX_BATCH) flush(); else schedule();
      return ev;
    }
    if (started && !sessionStarted) startSession();
    if (!opts.skipIdleRotation) rotateSessionIfIdle(now().getTime());
    if (event === "game_start") gamesPlayed += 1;
    if (event.startsWith("ad_")) props = adEventProps(win, props);
    const ev = enqueue(event, FIRST_TOUCH_EVENTS.includes(event) ? { ...ftProps, ...props } : props, opts);
    if (queue.length >= MAX_BATCH) flush();
    else schedule();
    return ev;
  }

  function schedule() {
    if (timer || !win?.setTimeout) return;
    timer = win.setTimeout(() => {
      timer = null;
      flush();
    }, env.flushMs ?? 5000);
  }

  function batches(items) {
    const out = [];
    let cur = [];
    let size = 12;
    for (const ev of items) {
      const len = JSON.stringify(ev).length + 1;
      if (cur.length && (cur.length >= MAX_BATCH || size + len > MAX_BEACON_BYTES)) {
        out.push(cur);
        cur = [];
        size = 12;
      }
      cur.push(ev);
      size += len;
    }
    if (cur.length) out.push(cur);
    return out;
  }

  /** Send everything queued. `beacon: true` uses navigator.sendBeacon (page hide/unload). */
  function flush({ beacon = false } = {}) {
    if (timer && win?.clearTimeout) win.clearTimeout(timer);
    timer = null;
    if (!reducedMode() && !consentAnalytics()) { queue.length = 0; return Promise.resolve(); }
    if (!queue.length) return Promise.resolve();
    const items = queue.splice(0, queue.length);
    const batchEpoch = consentEpoch;
    const canSend = (batch) => {
      const reduced = reducedMode();
      const normalAllowed = reduced ? false : consentAnalytics();
      if ((!reduced && !normalAllowed) || consentEpoch !== batchEpoch) return false;
      // Never add identity to an event created in reduced mode, including after a grant.
      for (const event of batch) {
        if (!event.consent_state && !normalAllowed) return false;
        if (!event.consent_state) event.anon_id = anon.id;
      }
      return true;
    };
    const sends = batches(items).map((batch) => {
      if (beacon && typeof nav?.sendBeacon === "function") {
        // A string body goes out as text/plain (no preflight); the server accepts it.
        try {
          if (canSend(batch) && nav.sendBeacon(endpoint, JSON.stringify({ events: batch }))) return Promise.resolve();
        } catch {
          /* fall through to fetch keepalive */
        }
      }
      if (!fetchImpl) return Promise.resolve();
      return Promise.resolve()
        .then(() => canSend(batch) ? fetchImpl(endpoint, {
          method: "POST",
          keepalive: true,
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ events: batch }),
        }) : null)
        .then((res) => {
          // 5xx / offline: put the batch back for the next flush. 4xx are not retried.
          if (res && res.status >= 500 && res.status !== 503 && canSend(batch)) queue.unshift(...batch);
        })
        .catch(() => {
          if (!beacon && canSend(batch)) queue.unshift(...batch);
        });
    });
    return Promise.all(sends).then(() => undefined);
  }

  function onHide(trigger, detail = {}) {
    for (const fn of [...hideHandlers]) {
      try {
        fn(trigger);
      } catch {
        /* never block the beacon */
      }
    }
    const t = now().getTime();
    visibility.sample(t);
    sessionVisibility.sample(t);
    // One session_end per session, sent on every leave: `hidden` (backgrounded — on Android
    // a tab killed there never fires pagehide, which is how the Oct 21 analysis lost 5 of
    // 111 ghost sessions) and again on `pagehide`. Both reuse one event_id, and the server
    // lets a later leave replace a stored `hidden` one, so the count does not change and the
    // final row is the real exit. `persisted` is the bfcache flag: a pagehide with it is a
    // page put aside, not a page closed.
    if (reducedMode() || pendingMeasurement() || consentAnalytics()) {
      if (started && !sessionStarted) startSession();
      track("session_end", {
        duration_s: Math.max(0, Math.round((t - sessionStartedAt) / 1000)),
        games_played: gamesPlayed,
        end_trigger: trigger === "pagehide" ? "pagehide" : "hidden",
        persisted: detail.persisted === true,
        ...visibilityProps(t),
      }, { eventId: sessionEndEventId, skipIdleRotation: true });
    }
    flush({ beacon: true });
  }

  function startSession() {
    if (sessionStarted) return;
    if (resetSession && !pendingMeasurement()) {
      sessionId = shortId("s", cryptoImpl);
      sessionEndEventId = uuid(cryptoImpl);
      sessionStartedAt = now().getTime();
      lastEventAt = sessionStartedAt;
      sessionVisibility.reset(sessionStartedAt);
      gamesPlayed = 0;
      resetSession = false;
    }
    if (reducedMode() || pendingMeasurement()) {
      track("session_start", { landing_page: safePath(loc?.pathname || "/").slice(0, 500),
        is_new_user: false, days_since_first_seen: 0,
        ...(visibility.stateAtLoad ? { vis_state: visibility.stateAtLoad } : {}), prerender: visibility.prerender });
      sessionStarted = true;
      sessionWasReduced = Boolean(reducedMode());
      return;
    }
    if (!consentAnalytics()) return;
    ({ firstTouch, sessionTouch } = storeTouch({ touch, nav, storage, sessionStore, now, win }));
    ftProps = firstTouchProps(firstTouch);
    sessionStarted = true;
    sessionWasReduced = false;
    enqueue("session_start", sessionStartProps());
    schedule();
  }

  function start() {
    if (started) { startSession(); return api; }
    started = true;
    // Both visibility watches have been listening since this tracker was built (above), so
    // a page opened before the application started is already on record.
    startSession();
    doc?.addEventListener?.("visibilitychange", () => {
      if (doc.visibilityState === "hidden") onHide("hidden");
    });
    win?.addEventListener?.("pagehide", (event) => onHide("pagehide", { persisted: event?.persisted === true }));
    return api;
  }

  const api = {
    start,
    track,
    flush,
    /** Register fn(trigger) to run before the hide/unload beacon; returns an unsubscribe. */
    onHide(fn) {
      hideHandlers.add(fn);
      return () => hideHandlers.delete(fn);
    },
    setAdContext(ctx) {
      childDirected = Boolean(ctx?.childDirected);
    },
    setMember(value) {
      isMember = Boolean(value);
    },
    get queue() {
      return queue;
    },
    get sessionId() {
      return sessionId;
    },
    get anonId() {
      return anon?.id ?? null;
    },
    consentAnalytics,
    /** The visitor's first-touch `ft_*` props (also on session_start and the sign-up events). */
    get firstTouch() {
      return { ...ftProps };
    },
    /** This visit's attribution (utm_* / share ref), carried past the landing URL. */
    get sessionTouch() {
      return { ...sessionTouch };
    },
    isVisible() {
      return !doc || doc.visibilityState !== "hidden";
    },
    /** Visibility at load, first visible moment and foreground time (createVisibilityWatch). */
    visibility,
    uuid: () => uuid(cryptoImpl),
    shortId: (prefix) => shortId(prefix, cryptoImpl),
    now,
    env: { win, doc, nav },
  };
  return api;
}

/** Game-scoped common props (plan: game_id, game_version, daily_seed, seed_mode). */
export function gameProps({ slug, version, day, mode }) {
  const props = { game_id: gameId(slug), daily_seed: day, seed_mode: mode === "daily" ? "daily" : "practice" };
  if (version) props.game_version = String(version).slice(0, 20);
  return props;
}

/**
 * Coarse device-width buckets for the load funnel (CSS px). A bucket, never the raw width:
 * enough to tell a small phone from a desktop without adding a fingerprinting signal.
 */
export const WIDTH_BUCKETS = Object.freeze(["xs", "sm", "md", "lg", "xl"]);
export function widthBucket(width) {
  const w = Math.round(Number(width));
  if (!Number.isFinite(w) || w <= 0) return null;
  return w < 360 ? "xs" : w < 480 ? "sm" : w < 768 ? "md" : w < 1024 ? "lg" : "xl";
}

/** Network Information API effective type, when the browser has one (Chrome/Android). */
export const NET_TYPES = Object.freeze(["slow-2g", "2g", "3g", "4g", "5g"]);
export function netType(nav) {
  let value;
  try {
    value = nav?.connection?.effectiveType ?? nav?.connection?.type;
  } catch {
    return null;
  }
  return typeof value === "string" && NET_TYPES.includes(value) ? value : null;
}

/**
 * How far one play-page view got: the load funnel we measure the Meta drop-off on.
 *
 * - `load_start` — the iframe's `src` was set.
 * - `ready` — the game reported `barumba:ready` (its own load is done).
 * - `armed` — the portal sent `init`: a run exists and, unless this view runs without one,
 *   the game's start card is up with its Play live. This is when `start_card_shown` goes
 *   out, and nothing is being played yet.
 * - `start` — the player did something deliberate: tapped Play on the start card, or made
 *   the first board input on a view that has no start card. That is the `game_start` event.
 *
 * Only a view that reached `start` played: every other view sends one `play_abandon` with
 * the step above that it reached.
 */
export const FUNNEL_STEPS = Object.freeze(["load_start", "ready", "armed", "start"]);
/**
 * What made `game_start` fire — the deliberate action, never the portal's own `init`:
 * `play_tap` (Play on the start card, reported by the game as `barumba:state` `playing`),
 * `first_input` (the first board input, which is the only deliberate action a view with no
 * start card has), or `restart` (a deliberate restart directly into gameplay).
 * A timer ending the run is not a player action.
 */
export const START_TRIGGERS = Object.freeze(["play_tap", "first_input", "restart"]);
/** Timings are capped at the server's limit (10 min); a longer wait is reported as 10 min. */
export const FUNNEL_MAX_MS = 600000;

/**
 * Lifecycle tracking for one play page: game_load_start → game_ready → start_card_shown →
 * game_start → play_heartbeat (every 60 s while visible) → game_end. If the tab is hidden
 * or closed mid-run, a game_end(outcome=quit) goes out by beacon; it reuses the run's
 * game_end event_id, so the real game_end replaces it server-side if the player comes back.
 *
 * **`game_start` is the player, not the portal** (KPI definition change, Oct 22: it used to
 * fire on `init`, which is now `start_card_shown`). Of 258 Meta "starters" on Oct 21 only
 * ~100 showed any evidence of playing, because the old event fired when the start card
 * appeared. It now fires once per run on the first deliberate action — Play tapped, or the
 * first board input when the view has no start card — and `start_card_shown` carries the old
 * meaning with the same properties, so a dashboard swaps the event name and the numbers do
 * not move.
 *
 * The funnel events also carry `elapsed_ms` since this view started, `nav_ms` since the
 * document's navigation start, the device width bucket, the connection type when the
 * browser offers one, whether an ad was really on screen, whether a consent prompt was up,
 * and the page's visibility (`page_visible` now, `load_visible` whether the document was on
 * screen when it loaded, `first_visible_ms` the moment it first got there, measured from
 * that same load) — the last two are what separate a prefetched Meta landing from a player
 * who left, so they keep the document's clock even when the view started later. A view the
 * player leaves before any deliberate action sends exactly one `play_abandon` with the step
 * it reached, on the hide beacon (sendBeacon) or on SPA navigation. One event per step, no
 * polling.
 *
 * Completion never synthesizes a player action: a timer can finish an untouched board.
 */
export function createRunTracker({
  tracker, slug, day, mode: initialMode, heartbeatMs = HEARTBEAT_MS,
  win = tracker.env.win, nav = tracker.env.nav ?? win?.navigator, perf = win?.performance,
  adShowing = () => false, consentShowing = () => consentPromptShowing(win),
}) {
  const loadId = tracker.shortId("l");
  const loadStartedAt = tracker.now().getTime();
  const visibility = tracker.visibility ?? null;
  /**
   * The document's own load, which is where the visibility record starts: the head bootstrap
   * (src/error-bootstrap.js) created this watch before any application chunk ran, so a
   * landing that was hidden at load and opened at 1.8 s has that moment on record even
   * though this view only started afterwards. Measuring from `loadStartedAt` instead would
   * subtract the later view start and clamp the moment away to 0.
   */
  const docStartedAt = Number.isFinite(visibility?.startedAt) ? visibility.startedAt : loadStartedAt;
  /**
   * Was the page on screen when the document loaded? A hidden (or pre-rendered) load is a
   * prefetch signal, and it stays on every funnel event of that document — including the
   * play view the player reaches after opening it. The view's own visibility at its start is
   * `page_visible` on `game_load_start`, sampled at that instant.
   */
  const loadVisible = visibility?.stateAtLoad ? visibility.stateAtLoad === "visible" : null;
  let version = null;
  let readySent = false;
  let run = null;
  let beatTimer = null;
  /** The furthest funnel step this view reached. */
  let reached = "load_start";
  /** True once the player did something deliberate (Play tap, first input, restart). */
  let played = false;
  let abandonSent = false;
  const conversionEventId = tracker.uuid();

  const base = (mode) => gameProps({ slug, version, day, mode });
  const capMs = (ms) => Math.min(FUNNEL_MAX_MS, Math.max(0, Math.round(ms)));

  /** Timing + context for one funnel step. Buckets and booleans only, no raw measurements. */
  function funnelProps() {
    const props = { elapsed_ms: capMs(tracker.now().getTime() - loadStartedAt), ad_showing: adShowing() === true };
    let navNow = null;
    try {
      navNow = perf?.now?.();
    } catch {
      navNow = null;
    }
    if (Number.isFinite(navNow)) props.nav_ms = capMs(navNow);
    const bucket = widthBucket(win?.innerWidth || win?.screen?.width);
    if (bucket) props.width_bucket = bucket;
    const net = netType(nav);
    if (net) props.net_type = net;
    // Only a known prompt state is reported: a page with no CMP says nothing at all, rather
    // than claiming no prompt was covering the game.
    let prompt = null;
    try {
      prompt = consentShowing();
    } catch {
      prompt = null;
    }
    if (typeof prompt === "boolean") props.consent_showing = prompt;
    // Visibility, the signal the Oct 21 Meta analysis was missing: whether the page is on
    // screen at this step, whether the document was when it loaded, and when it first got
    // there (0 for a page that loaded visible; absent while it never has been). The last two
    // describe the document, so they are measured from its load (`docStartedAt`) and not
    // from this view — `elapsed_ms` is the per-view clock.
    if (visibility) {
      const stateNow = visibility.state();
      if (stateNow) props.page_visible = stateNow === "visible";
      if (loadVisible !== null) props.load_visible = loadVisible;
      if (visibility.firstVisibleAt !== null) props.first_visible_ms = capMs(visibility.firstVisibleAt - docStartedAt);
    }
    return props;
  }

  /**
   * The player left the play page before doing anything deliberate: which step the view
   * reached and how long that took. Once per view, and never once the view was played.
   */
  function abandon(trigger) {
    if (played || abandonSent) return null;
    abandonSent = true;
    return tracker.track("play_abandon", { ...base(initialMode), load_id: loadId, step_reached: reached, end_trigger: trigger, ...funnelProps() }, { eventId: conversionEventId });
  }

  tracker.track("game_load_start", { ...base(initialMode), load_id: loadId, ...funnelProps() });

  function elapsedS() {
    return run ? Math.max(0, Math.round((tracker.now().getTime() - run.startedAt) / 1000)) : 0;
  }

  /**
   * The run is really being played: one `game_start` per run, on the first deliberate
   * action. From here the view is a play, never an abandonment. `armed_ms` is how long the
   * playable start card sat untouched — `elapsed_ms` counts the load as well, so this is
   * the one field that tells a slow load from a player who looked at Play for eight seconds.
   */
  function markStart(trigger) {
    if (!run || run.startSent) return null;
    const firstPlay = !played;
    played = true;
    reached = "start";
    run.startSent = true;
    const at = tracker.now().getTime();
    const props = { ...base(run.mode), run_id: run.id, attempt_no: run.attemptNo, load_id: loadId, start_trigger: trigger, ...funnelProps() };
    if (trigger === "first_input") props.first_input_ms = Math.min(600000, capMs(at - run.openedAt));
    if (run.armedAt !== null) props.armed_ms = capMs(at - run.armedAt);
    // The run's own clock starts here, not when the card was armed: a `duration_s` that
    // included the wait on an untouched start card would inflate every heartbeat and quit.
    run.startedAt = at;
    return tracker.track("game_start", props, firstPlay ? { eventId: conversionEventId } : {});
  }

  function sendEnd({ outcome, score, trigger, final }) {
    if (!run) return null;
    const ev = tracker.track(
      "game_end",
      { ...base(run.mode), run_id: run.id, outcome, duration_s: run.durationS ?? elapsedS(), score: Math.max(0, Math.round(Number(score) || 0)), end_trigger: trigger },
      { eventId: run.endEventId },
    );
    if (final) {
      stopBeats();
      run = null;
    }
    return ev;
  }

  function stopBeats() {
    if (beatTimer && win?.clearInterval) win.clearInterval(beatTimer);
    beatTimer = null;
  }

  const offHide = tracker.onHide((trigger) => {
    // Left (or backgrounded) before the player started: that is the load drop-off we measure.
    abandon(trigger === "pagehide" ? "pagehide" : "hidden");
    if (!run?.startSent || run.quitSent) return;
    run.quitSent = true;
    sendEnd({ outcome: "quit", score: run.score, trigger, final: false });
  });

  return {
    loadId,
    get run() {
      return run;
    },
    /** The furthest load-funnel step this view reached (see FUNNEL_STEPS). */
    get step() {
      return reached;
    },
    /**
     * The game reported a run running (`barumba:state` `playing`). On a view with a start
     * card that is the player's Play tap, so it is the run's `game_start`. On a view that
     * runs with no start card the game is running from the moment it loads — nobody has
     * done anything yet — so this reports nothing and the first board input does it
     * instead. Idempotent: a pause → resume reports `playing` again and adds nothing.
     */
    playing() {
      if (!run?.card || run.playTapSent) return null;
      run.playTapSent = true;
      tracker.track("play_tap", { ...base(run.mode), run_id: run.id, load_id: loadId, ...funnelProps() });
      return markStart("play_tap");
    },
    /** A deliberate restart bypasses the start card and needs no board input. */
    restart() {
      return markStart("restart");
    },
    /**
     * The game reported the player's first board input (`barumba:state` with
     * `firstInput: true`). The deliberate start of a view that shows no start card, and a
     * backstop for one that does (a player who presses the board straight away).
     */
    firstInput() {
      if (!run || run.firstInputSent) return null;
      run.firstInputSent = true;
      tracker.track("first_input", { ...base(run.mode), run_id: run.id, load_id: loadId, ...funnelProps() });
      return markStart("first_input");
    },
    ready(v) {
      if (v) version = String(v);
      if (readySent) return;
      readySent = true;
      if (reached === "load_start") reached = "ready";
      tracker.track("game_ready", { ...base(initialMode), game_version: version || "unknown", load_id: loadId, load_ms: capMs(tracker.now().getTime() - loadStartedAt), ...funnelProps() });
    },
    /**
     * Create a run; `card: true` reports that its playable start card is presented.
     * `card: false` for a view that starts the player straight into the board with no card
     * — only the first input counts as a start. `defer` also records the cardless
     * exposure decision with shown:false for the portal friction report.
     */
    start({ mode, attemptNo, defer = false, card = !defer }) {
      if (run?.startSent) sendEnd({ outcome: "quit", score: run.score, trigger: "restart", final: true });
      const at = tracker.now().getTime();
      const attempt = Math.max(1, attemptNo || 1);
      run = { id: tracker.shortId("r"), endEventId: tracker.uuid(), mode, attemptNo: attempt, openedAt: at, startedAt: at, score: 0, beats: 0, quitSent: false, card: card !== false, armedAt: card !== false ? at : null, startSent: false };
      if (!version) version = "unknown";
      // The old `game_start`, under its real name and with its own properties: the start
      // card is on screen and playable. load_id joins it to its own view's load_start /
      // ready / game_start / play_abandon — repeated views and restarts of the same game
      // share game_id and run ids only, so without it the funnel cannot be reassembled.
      if (run.card || defer) tracker.track("start_card_shown", { ...base(mode), run_id: run.id, attempt_no: attempt, load_id: loadId, ...(defer ? { shown: false } : {}), ...funnelProps() });
      // Arming only puts a card on screen, so this step never suppresses play_abandon.
      if (!played) reached = "armed";
      stopBeats();
      if (win?.setInterval) {
        beatTimer = win.setInterval(() => {
          // A run nobody has started yet is not being played: an untouched start card must
          // not beat (heartbeats are the plan's evidence that a run is under way).
          if (!run?.startSent || !tracker.isVisible()) return;
          run.beats += 1;
          tracker.track("play_heartbeat", { ...base(run.mode), run_id: run.id, beat_no: run.beats, duration_s: elapsedS() });
        }, heartbeatMs);
      }
      return run.id;
    },
    score(value) {
      if (run) run.score = Number(value) || 0;
    },
    /** barumba:runEnd arrived: the run finished (lives out → fail, otherwise complete). */
    end(msg = {}) {
      if (!run) return null;
      const id = run.id;
      // A timed game may finish with no input. Do not invent a deliberate start here.
      run.durationS = Math.round((Number(msg.durationMs) || 0) / 1000);
      const outcome = msg.stats && msg.stats.livesLeft === 0 ? "fail" : "complete";
      sendEnd({ outcome, score: msg.score, trigger: "run_end", final: true });
      return id;
    },
    /** Leaving the play page (SPA navigation) mid-run. */
    destroy() {
      // Left before the player started (Back, "All games", a tile): the same drop-off.
      abandon("navigate");
      if (run?.startSent) sendEnd({ outcome: "quit", score: run.score, trigger: "navigate", final: true });
      run = null;
      stopBeats();
      offHide();
      tracker.flush();
    },
  };
}

/** A tile counts as seen at ≥50% visible (IntersectionObserver threshold). */
export const TILE_RATIO = 0.5;
/** …and only after it has stayed that visible for a full second. */
export const TILE_DWELL_MS = 1000;

/** `shelf|game|position` — one impression per tile per page view (docs/tile-events.md). */
export const tileKey = (node) => `${node.dataset.shelf}|${node.dataset.game}|${node.dataset.pos}`;

/**
 * tile_impression and tile_click for every `[data-track-tile]` inside `root`.
 *
 * Impression: the tile has been ≥50% visible (`TILE_RATIO`) continuously for 1 s
 * (`TILE_DWELL_MS`) *with the tab in the foreground*; dropping below 50% cancels it, and so
 * does the tab being hidden — coming back starts a fresh second, because IntersectionObserver
 * reports geometry only and would otherwise count a tile that sat behind another tab. A click
 * counts as an impression too (it implies the tile was seen), so a tile tapped straight away
 * is never missed. Either way a tile is counted once per page view: pass the same `seen` Set
 * for re-renders of one view. Clicks are not deduped — every activation is one tile_click.
 *
 * Without `IntersectionObserver` visibility cannot be measured at all, so no dwell is run:
 * impressions come only from clicks (an unseen tile must never be counted as seen).
 *
 * Tiles carry data-game (slug), data-shelf, data-pos (1-based), data-mode and, optionally,
 * data-page to override the page name. Nothing is sent without analytics consent: every
 * event goes through `tracker.track`, which is the consent gate.
 *
 * Returns a cleanup function.
 */
export function trackTiles(root, { tracker, day, page, seen = new Set(), dwellMs = TILE_DWELL_MS, ratio = TILE_RATIO } = {}) {
  if (!root?.querySelectorAll) return () => {};
  const win = tracker.env.win;
  const doc = tracker.env.doc;
  const setTimer = win?.setTimeout ? win.setTimeout.bind(win) : ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = win?.clearTimeout ? win.clearTimeout.bind(win) : ((handle) => clearTimeout(handle));
  const tiles = [...root.querySelectorAll("[data-track-tile]")];
  /** Tiles waiting out their dwell second. */
  const timers = new Map();
  /** Tiles currently ≥ratio visible by geometry, whether or not the tab is in front. */
  const onScreen = new Set();
  /** True while the tab is in the background: dwell time there doesn't count. */
  const tabHidden = () => doc?.visibilityState === "hidden";
  const props = (node) => ({
    ...gameProps({ slug: node.dataset.game, day, mode: node.dataset.mode }),
    shelf: node.dataset.shelf,
    position: Number(node.dataset.pos) || 1,
    page: node.dataset.page || page,
  });
  const cancel = (node) => {
    if (!timers.has(node)) return;
    clearTimer(timers.get(node));
    timers.delete(node);
  };
  const impress = (node) => {
    cancel(node);
    const key = tileKey(node);
    if (seen.has(key)) return false;
    seen.add(key);
    onScreen.delete(node);
    observer?.unobserve(node);
    tracker.track("tile_impression", props(node));
    return true;
  };
  /** Start the dwell second (foreground only); a pending one keeps running. */
  const startDwell = (node) => {
    if (seen.has(tileKey(node)) || timers.has(node) || tabHidden()) return;
    timers.set(node, setTimer(() => {
      timers.delete(node);
      impress(node);
    }, dwellMs));
  };
  // ≥50% visible: start (or keep) the dwell timer. Below 50%: cancel it, so the second has
  // to be continuous.
  const enter = (node) => {
    onScreen.add(node);
    startDwell(node);
  };
  const leave = (node) => {
    onScreen.delete(node);
    cancel(node);
  };
  let observer = null;
  if (win?.IntersectionObserver) {
    observer = new win.IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= ratio) enter(entry.target);
        else leave(entry.target);
      }
    }, { threshold: [0, ratio] });
    tiles.forEach((tile) => observer.observe(tile));
  }
  // Hidden tab: drop every pending second (geometry says "visible" even behind another tab).
  // Back in front: the still-on-screen tiles each need a fresh continuous second.
  const onVisibility = () => {
    if (tabHidden()) for (const node of [...timers.keys()]) cancel(node);
    else for (const node of onScreen) startDwell(node);
  };
  doc?.addEventListener?.("visibilitychange", onVisibility);
  const onClick = (event) => {
    const tile = event.target?.closest?.("[data-track-tile]");
    if (!tile || !root.contains(tile)) return;
    impress(tile); // a click implies the tile was seen
    tracker.track("tile_click", props(tile));
  };
  // Results can navigate as soon as an ad promise settles. Capture the tile
  // before between-rounds handlers can tear down this listener.
  const capture = page === "results";
  root.addEventListener("click", onClick, capture);
  return () => {
    observer?.disconnect();
    for (const node of [...timers.keys()]) cancel(node);
    onScreen.clear();
    doc?.removeEventListener?.("visibilitychange", onVisibility);
    root.removeEventListener("click", onClick, capture);
  };
}

let shared = null;
/** The page's tracker (started on first use in the browser). */
/** Discord invite link placements (server allowlist: discord_click.placement). */
export const DISCORD_PLACEMENTS = Object.freeze(["footer", "gameover"]);

/**
 * discord_click props for a clicked link, or null when it is not a Discord invite link.
 * Placement comes from data-discord-placement (else the link's utm_medium); game_id from
 * data-game-id (game-over screen only).
 */
export function discordClickProps(anchor, loc) {
  const href = anchor?.getAttribute?.("href") || "";
  let url;
  try {
    url = new URL(href, "https://barumba.gg");
  } catch {
    return null;
  }
  if (!/(^|\.)discord\.(gg|com)$/i.test(url.hostname)) return null;
  const placement = anchor.getAttribute("data-discord-placement") || url.searchParams.get("utm_medium");
  if (!DISCORD_PLACEMENTS.includes(placement)) return null;
  const props = { placement, page: safePath(loc?.pathname || "/").slice(0, 500) };
  const game = anchor.getAttribute("data-game-id");
  if (game) props.game_id = game;
  return props;
}

/**
 * One delegated click listener: a Discord link click queues discord_click (consent is
 * checked inside track()) and flushes it with sendBeacon. Never prevents or delays the
 * navigation (the links open in a new tab). Returns an uninstall function.
 */
export function installDiscordTracking({ doc = globalThis.document, tracker, loc = globalThis.location } = {}) {
  if (!doc?.addEventListener || !tracker) return () => {};
  const onClick = (e) => {
    try {
      const a = e?.target?.closest?.("a[href]");
      const props = a && discordClickProps(a, loc);
      if (!props) return;
      if (tracker.track("discord_click", props)) tracker.flush({ beacon: true });
    } catch {
      /* analytics never blocks a click */
    }
  };
  doc.addEventListener("click", onClick, true);
  return () => doc.removeEventListener("click", onClick, true);
}

/**
 * lead_tap props for a click on a category lander's "Play today's puzzle" button
 * (`a[data-lead-play="<slug>"]`, site/pages.js categoryLeadHtml), or null for any other click.
 */
export function leadTapProps(anchor, loc) {
  const slug = anchor?.getAttribute?.("data-lead-play") || "";
  if (!/^[a-z0-9-]{1,60}$/.test(slug)) return null;
  return { game_id: gameId(slug), page: safePath(loc?.pathname || "/").slice(0, 500) };
}

/**
 * One delegated click listener on a category lander: a tap on the lead button queues
 * lead_tap (consent is checked inside track()) and flushes it with sendBeacon, which survives
 * the navigation to /play/<slug>. Never prevents or delays the click. Returns an uninstall
 * function.
 */
export function installLeadTracking({ doc = globalThis.document, tracker, loc = globalThis.location } = {}) {
  if (!doc?.addEventListener || !tracker) return () => {};
  const onClick = (e) => {
    try {
      const a = e?.target?.closest?.("a[data-lead-play]");
      const props = a && leadTapProps(a, loc);
      if (!props) return;
      if (tracker.track("lead_tap", props)) tracker.flush({ beacon: true });
    } catch {
      /* analytics never blocks a click */
    }
  };
  doc.addEventListener("click", onClick, true);
  return () => doc.removeEventListener("click", onClick, true);
}

export function getTracker() {
  if (!shared) shared = (globalThis.window?.__barumbaErrors?.getTracker() || createTracker()).start();
  return shared;
}
